import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ArrowRight, Hash, ChevronDown, Check, Search } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { BRANDS, markers } from '../utils/data';
import { usesOldNewCodes } from '../utils/color';
import PageHelpModal from '../components/PageHelpModal';

/**
 * Color conversion and distance calculation utilities
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

function rgbToXyz(rgb) {
  let r = rgb.r / 255;
  let g = rgb.g / 255;
  let b = rgb.b / 255;

  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

  const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.0721750;
  const z = r * 0.0193339 + g * 0.1191920 + b * 0.9503041;

  return { x: x * 100, y: y * 100, z: z * 100 };
}

function xyzToLab(xyz) {
  const refX = 95.047;
  const refY = 100.000;
  const refZ = 108.883;

  let x = xyz.x / refX;
  let y = xyz.y / refY;
  let z = xyz.z / refZ;

  const delta = 6 / 29;
  const t0 = delta * delta * delta;
  const m = 1 / (3 * delta * delta);
  const c = 4 / 29;

  x = x > t0 ? Math.pow(x, 1 / 3) : m * x + c;
  y = y > t0 ? Math.pow(y, 1 / 3) : m * y + c;
  z = z > t0 ? Math.pow(z, 1 / 3) : m * z + c;

  return { L: 116 * y - 16, a: 500 * (x - y), b: 200 * (y - z) };
}

function hexToLab(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return xyzToLab(rgbToXyz(rgb));
}

function colorDistanceRGB(hex1, hex2) {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return Infinity;
  const rDiff = rgb1.r - rgb2.r;
  const gDiff = rgb1.g - rgb2.g;
  const bDiff = rgb1.b - rgb2.b;
  return Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff);
}

function colorDistanceDeltaE(hex1, hex2) {
  const lab1 = hexToLab(hex1);
  const lab2 = hexToLab(hex2);
  if (!lab1 || !lab2) return Infinity;
  const deltaL = lab1.L - lab2.L;
  const deltaA = lab1.a - lab2.a;
  const deltaB = lab1.b - lab2.b;
  return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
}

function getMatchPercentage(distance, algorithm) {
  const MAX_DISTANCE = algorithm === 'rgb' ? 150 : 50;
  if (distance === 0) return 100;
  return Math.round(Math.max(0, 100 - (distance / MAX_DISTANCE * 100)));
}


function getTextColor(hex) {
  const clean = (hex || '').replace('#', '');
  if (clean.length < 6) return '#1e293b';
  const r = parseInt(clean.substr(0, 2), 16);
  const g = parseInt(clean.substr(2, 2), 16);
  const b = parseInt(clean.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#1e293b' : '#ffffff';
}

function getScoreColor(pct) {
  if (pct >= 95) return 'text-emerald-500';
  if (pct >= 85) return 'text-emerald-600';
  if (pct >= 70) return 'text-amber-500';
  if (pct >= 50) return 'text-orange-500';
  return 'text-slate-400';
}

function ResultCard({ marker, matchPct, displayCode, isBest }) {
  const textColor = getTextColor(marker.hex);
  return (
    <div className={`rounded-2xl border shadow-sm p-3 flex items-center gap-3 ${isBest ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-slate-100'}`}>
      <div className="w-16 h-16 rounded-xl shrink-0 shadow-sm relative" style={{ backgroundColor: marker.hex }}>
        <span
          className="absolute bottom-2 left-2 text-[11px] font-mono font-semibold leading-tight"
          style={{ color: textColor }}
        >
          {displayCode}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{marker.name || displayCode}</p>
        <p className="text-xs text-slate-400 truncate">{marker.brand}</p>
      </div>
      {matchPct != null && (
        <div className="text-right shrink-0">
          <p className={`text-sm font-bold ${getScoreColor(matchPct)}`}>{matchPct}%</p>
          <p className="text-[10px] text-slate-400">match</p>
        </div>
      )}
    </div>
  );
}

function isValidHex(hex) {
  return /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.test(hex);
}

function normalizeHex(hex) {
  const trimmed = hex.trim();
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function CustomSelect({ value, onChange, options }) {
  const [open, setOpen] = React.useState(false);
  const ref = useRef(null);
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
      >
        <span className="truncate">{value}</span>
        <ChevronDown size={13} className={`flex-shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-2 bg-white/95 backdrop-blur-md rounded-xl shadow-soft-xl border border-slate-100 py-2 z-50 animate-fadeIn max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full px-4 py-1.5 text-left text-xs flex items-center justify-between hover:bg-slate-50 transition-colors ${value === opt ? 'text-rose-pastel-600 font-semibold' : 'text-slate-700'}`}
            >
              <span className="truncate">{opt}</span>
              {value === opt && <Check size={12} className="text-rose-pastel-500 flex-shrink-0 ml-2" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HexFinderPage() {
  const { t } = useI18n();

  const [hexInput, setHexInput] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('Ohuhu (Old Code)');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [algorithm, setAlgorithm] = useState('rgb');
  const [submittedHex, setSubmittedHex] = useState(null);
  const [hexError, setHexError] = useState('');

  const availableBrands = useMemo(() => {
    const baseBrands = BRANDS.filter(b => b !== 'All' && b !== 'My Collection');
    const extendedBrands = [];
    baseBrands.forEach(brand => {
      if (usesOldNewCodes(brand)) {
        extendedBrands.push(`${brand} (Old Code)`, `${brand} (New Code)`);
      } else {
        extendedBrands.push(brand);
      }
    });
    return extendedBrands;
  }, []);

  const parseBrand = (brandString) => {
    if (brandString.endsWith(' (Old Code)')) {
      return { brand: brandString.slice(0, -' (Old Code)'.length), codeType: 'old' };
    }
    if (brandString.endsWith(' (New Code)')) {
      return { brand: brandString.slice(0, -' (New Code)'.length), codeType: 'new' };
    }
    return { brand: brandString, codeType: null };
  };

  const getDisplayCode = (marker, brandString) => {
    const { codeType } = parseBrand(brandString);
    if (usesOldNewCodes(marker.brand)) {
      if (codeType === 'old') return marker.code_old || '-';
      if (codeType === 'new') return marker.code_new || '-';
      return `${marker.code_old || '-'} / ${marker.code_new || '-'}`;
    }
    return marker.code || '-';
  };

  const handleSearch = async (hexValue, brandValue, algorithmValue) => {
    const hex = hexValue ?? hexInput;
    const brand = brandValue ?? selectedBrand;
    const algo = algorithmValue ?? algorithm;

    if (!isValidHex(hex)) {
      setHexError(t('hexFinder.invalidHex'));
      return;
    }

    setHexError('');
    const normalizedHex = normalizeHex(hex);
    setSubmittedHex(normalizedHex);
    setIsSearching(true);
    setResults([]);

    await new Promise(resolve => setTimeout(resolve, 600));

    const { brand: brandName, codeType } = parseBrand(brand);
    let brandMarkers = markers.filter(m => m.brand === brandName);

    if (usesOldNewCodes(brandName) && codeType) {
      brandMarkers = brandMarkers.filter(m => {
        if (codeType === 'old') return m.code_old && m.code_old !== '-';
        return m.code_new && m.code_new !== '-';
      });
    }

    const colorDistanceFn = algo === 'rgb' ? colorDistanceRGB : colorDistanceDeltaE;

    const sorted = brandMarkers
      .map(m => ({ ...m, distance: colorDistanceFn(normalizedHex, m.hex) }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 10);

    setResults(sorted);
    setIsSearching(false);
  };

  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    setResults([]);
    setSubmittedHex(null);
  };

  // Re-run search when algorithm changes (if there's already a submitted hex)
  useEffect(() => {
    if (submittedHex && results.length > 0) {
      handleSearch(submittedHex, selectedBrand, algorithm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algorithm]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div className="w-full mx-auto sm:px-6 lg:px-8 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl md:text-4xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-teal-400 to-emerald-500">
          {t('hexFinder.title')} <Hash className="inline-block w-8 h-8 pb-1 text-emerald-500" />
        </h2>
        <p className="text-base md:text-md text-elegant max-w-2xl mx-auto">
          {t('hexFinder.subtitle')}
        </p>
        <PageHelpModal
          title={t('hexFinder.help.title')}
          tips={[
            { text: t('hexFinder.help.tip1') },
            { text: t('hexFinder.help.tip2') },
            { text: t('hexFinder.help.tip3') },
            { text: t('hexFinder.help.tip4') },
          ]}
          trigger={
            <p className="text-sm justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer animate-pulse mt-2 mx-auto inline-flex items-center gap-2">
              {t('common.needHelp')}
            </p>
          }
        />
      </div>

      {/* Brand Selection + HEX Input + Algorithm */}
      <div className="grid gap-6 lg:grid-cols-[1fr_1fr_auto]">
        {/* Brand */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
            {t('hexFinder.brand')}
          </label>
          <CustomSelect value={selectedBrand} onChange={handleBrandChange} options={availableBrands} />
        </div>

        {/* HEX Input */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
            {t('hexFinder.hexLabel')}
          </label>
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
              <input
                type="text"
                value={hexInput}
                onChange={(e) => {
                  setHexInput(e.target.value);
                  setHexError('');
                }}
                onKeyDown={handleKeyDown}
                placeholder={t('hexFinder.hexPlaceholder')}
                maxLength={7}
                className={`w-full pl-9 pr-4 py-2 text-xs border rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 transition-all ${hexError ? 'border-red-300' : 'border-slate-200'}`}
              />
            </div>
            {/* Color preview swatch */}
            {isValidHex(hexInput) && (
              <div
                className="self-stretch w-12 rounded-xl border border-slate-200 flex-shrink-0"
                style={{ backgroundColor: normalizeHex(hexInput) }}
              />
            )}
          </div>
          {hexError && (
            <p className="text-xs text-red-500">{hexError}</p>
          )}
        </div>

        {/* Algorithm Toggle */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
            {t('hexFinder.algorithm')}
          </label>
          <div className="flex items-center gap-3 h-[50px]">
            <span className={`text-sm font-semibold transition-all duration-300 ${
              algorithm === 'rgb' ? 'text-blue-600 scale-110' : 'text-slate-400 scale-100'
            }`}>
              RGB
            </span>
            <div
              className={`relative w-14 h-7 rounded-full cursor-pointer transition-all duration-300 ${
                algorithm === 'rgb'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 shadow-md shadow-blue-200'
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 shadow-md shadow-purple-200'
              }`}
              onClick={() => setAlgorithm(algorithm === 'rgb' ? 'deltaE' : 'rgb')}
            >
              <div
                className={`absolute top-0.5 bg-white rounded-full shadow-lg transition-all duration-300 ease-in-out ${
                  algorithm === 'deltaE' ? 'left-[30px] w-6 h-6 scale-110' : 'left-0.5 w-6 h-6 scale-110'
                }`}
                style={{ transition: 'left 0.3s ease-in-out, width 0.3s ease-in-out, transform 0.3s ease-in-out' }}
              />
            </div>
            <span className={`text-sm font-semibold transition-all duration-300 ${
              algorithm === 'deltaE' ? 'text-purple-600 scale-110' : 'text-slate-400 scale-100'
            }`}>
              Delta E
            </span>
          </div>
        </div>
      </div>

      {/* Search Button */}
      <div className="flex justify-center">
        <button
          onClick={() => handleSearch()}
          disabled={isSearching}
          className="flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white font-semibold shadow-lg shadow-teal-200 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Search size={20} />
          {t('hexFinder.searchButton')}
        </button>
      </div>

      {/* Results */}
      {(isSearching || (submittedHex && results.length >= 0)) && (
        <div className="space-y-4">
          {isSearching ? (
            <div className="flex flex-col items-center justify-center py-16 rounded-2xl bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200">
              <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mb-4" />
              <p className="text-slate-600 font-medium">{t('hexFinder.searching')}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center text-slate-500 py-16 rounded-2xl bg-slate-50 border border-slate-200">
              {t('hexFinder.noResults')}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Your colour */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl shrink-0 shadow-sm" style={{ backgroundColor: submittedHex }} />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 mb-0.5">{t('hexFinder.yourColor')}</p>
                  <p className="text-sm font-semibold text-slate-900 font-mono">{submittedHex.toUpperCase()}</p>
                </div>
              </div>

              {/* Down arrow */}
              <div className="flex justify-center">
                <ArrowRight size={16} className="text-slate-300 rotate-90" />
              </div>

              {/* All matches */}
              {results.map((result, idx) => (
                <ResultCard
                  key={`${result.brand}-${result.code || result.code_old || result.code_new || idx}`}
                  marker={result}
                  matchPct={getMatchPercentage(result.distance, algorithm)}
                  displayCode={getDisplayCode(result, selectedBrand)}
                  isBest={idx === 0}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
